import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  MoreHorizontal,
  Play,
  Pause,
  Trash2,
  FileText,
  Check,
  CirclePlus,
  ChevronRight,
  ChevronDown,
  Loader2,
  Flag,
  Target,
  Newspaper,
  Gavel,
  Sparkles,
} from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { fmtRelative, fmtDateTime, templatesMeta } from "../lib/format";
import ProjectFormEditor from "./ProjectForm";
import ResultModal from "../components/ResultModal";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

function stageLabel(status) {
  switch (status) {
    case "in_progress": return "In Progress";
    case "completed": return "Completed";
    default: return "Not Started";
  }
}

function stageStatusClass(status) {
  switch (status) {
    case "in_progress": return "bg-[#EFF6FF] text-[#1D4ED8] border-[#1D4ED8]";
    case "completed": return "bg-[#ECFDF5] text-[#047857] border-[#047857]";
    default: return "bg-[#F1F5F9] text-[#64748B] border-[#CBD5E1]";
  }
}

function formatScheduleTime(hour, minute, timezone) {
  const numericHour = Number(hour);
  const period = numericHour >= 12 ? "PM" : "AM";
  const displayHour = numericHour % 12 || 12;
  return `${String(displayHour).padStart(2, "0")}:${String(Number(minute) || 0).padStart(2, "0")} ${period} (${timezone || "UTC"})`;
}

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isOrgMember = user?.role === "org_member";
  const isAdmin = user?.role === "org_admin" || user?.role === "orl_admin";

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [tracker, setTracker] = useState({ stages: [], notes: [] });
  const [orgMembers, setOrgMembers] = useState([]);
  const [recentUpdates, setRecentUpdates] = useState([]);
  const [results, setResults] = useState([]);
  const [resultTotal, setResultTotal] = useState(0);
  const [newsTotal, setNewsTotal] = useState(0);
  const [tenderTotal, setTenderTotal] = useState(0);
  const [openRecommendationTotal, setOpenRecommendationTotal] = useState(0);
  const [selectedStageId, setSelectedStageId] = useState(null);
  const [busy, setBusy] = useState(null); // "run"|"pause"|"delete"|stageId|milestoneId
  const [openStageMenu, setOpenStageMenu] = useState(false);
  const [openStageActionMenu, setOpenStageActionMenu] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [runConfirm, setRunConfirm] = useState(false);
  const [activateConfirm, setActivateConfirm] = useState(false);
  const [completeStageConfirm, setCompleteStageConfirm] = useState(null); // stageId
  const [pendingStartStage, setPendingStartStage] = useState(null);
  const [pendingDeleteMilestone, setPendingDeleteMilestone] = useState(null);
  const [pendingDeleteStage, setPendingDeleteStage] = useState(null);
  const [pendingDeleteNote, setPendingDeleteNote] = useState(null);
  const [pendingEditStage, setPendingEditStage] = useState(null);
  const [activeResult, setActiveResult] = useState(null);
  const [pendingAddMilestoneStage, setPendingAddMilestoneStage] = useState(null);
  const [pendingAddStage, setPendingAddStage] = useState(false);
  const [newStageName, setNewStageName] = useState("");
  const [newStageDescription, setNewStageDescription] = useState("");
  const [newStageDueDate, setNewStageDueDate] = useState("");
  const [newMilestoneName, setNewMilestoneName] = useState("");
  const [newMilestoneDueDate, setNewMilestoneDueDate] = useState("");
  const [pendingAddNote, setPendingAddNote] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [taggedMemberIds, setTaggedMemberIds] = useState([]);
  const [memberDropdownOpen, setMemberDropdownOpen] = useState(false);
  const memberDropdownRef = useRef(null);
  const [noteBusy, setNoteBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const responses = await Promise.allSettled([
        api.get(`/projects/${id}`).then((r) => r.data),
        api.get(`/projects/${id}/tracker`).then((r) => r.data),
        api.get(`/results`, { params: { project_id: id, status: "kept", sort: "newest", limit: 100 } }).then((r) => r.data),
        api.get(`/tenders`, { params: { project_id: id } }).then((r) => r.data),
        api.get(`/results`, { params: { project_id: id, action_required: true, action_status: "open", limit: 1 } }).then((r) => r.data),
        api.get(`/tenders`, { params: { project_id: id, action_status: "open" } }).then((r) => r.data),
        api.get(`/projects/${id}/tracker/recent-updates`).then((r) => r.data.items || []),
        api.get("/org/members").then((r) => r.data.members || []),
      ]);
      const [projectResponse, trackerResponse, resultsResponse, tendersResponse, recommendationsResponse, tenderRecommendationsResponse, updatesResponse, membersResponse] = responses;

      if (projectResponse.status === "rejected") {
        throw projectResponse.reason;
      }

      setProject(projectResponse.value);
      if (trackerResponse.status === "fulfilled") setTracker(trackerResponse.value);
      if (resultsResponse.status === "fulfilled") {
        setResults(resultsResponse.value.items || []);
        setResultTotal(resultsResponse.value.total || 0);
        setNewsTotal(resultsResponse.value.news_total || 0);
      }
      if (tendersResponse.status === "fulfilled") {
        setTenderTotal(tendersResponse.value.count || tendersResponse.value.tenders?.length || 0);
      }
      const openNewsRecommendations = recommendationsResponse.status === "fulfilled"
        ? recommendationsResponse.value.total || recommendationsResponse.value.items?.length || 0
        : 0;
      const openTenderRecommendations = tenderRecommendationsResponse.status === "fulfilled"
        ? tenderRecommendationsResponse.value.count || tenderRecommendationsResponse.value.tenders?.length || 0
        : 0;
      setOpenRecommendationTotal(openNewsRecommendations + openTenderRecommendations);
      if (updatesResponse.status === "fulfilled") setRecentUpdates(updatesResponse.value);
      if (membersResponse.status === "fulfilled") setOrgMembers(membersResponse.value);

    } catch (e) {
      toast.error("Failed to load project");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!memberDropdownOpen) return;
    const handlePointerDown = (event) => {
      if (memberDropdownRef.current && !memberDropdownRef.current.contains(event.target)) {
        setMemberDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [memberDropdownOpen]);

  const running = project?.is_running || busy === "run";
  const isDraft = project?.status === "draft";
  const isPaused = project?.is_active === false && project?.status !== "draft";

  const stages = useMemo(() => tracker.stages || [], [tracker]);
  const notes = tracker.notes || [];
  const milestones = useMemo(() => {
    const byStage = {};
    stages.forEach((s) => { byStage[s.id] = s.milestones || []; });
    return byStage;
  }, [stages]);

  const currentStage = stages.find((s) => s.status === "in_progress");
  const firstStage = stages[0];

  const now = Date.now();

  const isOverdue = (due) => {
    if (!due) return false;
    try { return new Date(due).getTime() < now; } catch { return false; }
  };

  // ---- project actions ----
  const handleRun = async () => {
    setRunConfirm(false);
    setBusy("run");
    try {
      const r = await api.post(`/projects/${id}/run`);
      if (r.data.ok) toast.success(`Done, ${r.data.kept} new results kept`);
      else toast.error(r.data.error || "Run failed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed");
    } finally { setBusy(null); load(); }
  };

  const handleToggleActive = async () => {
    const next = !project.is_active;
    setBusy("pause");
    try {
      await api.patch(`/projects/${id}`, { is_active: next });
      toast.success(next ? "Project resumed" : "Project paused — pipeline will skip it");
      load();
    } catch (e) { toast.error("Could not update status"); } finally { setBusy(null); }
  };

  const handleActivateDraft = async () => {
    setActivateConfirm(false);
    setBusy("pause");
    try {
      await api.patch(`/projects/${id}`, { status: "active", is_active: true });
      toast.success("Draft activated — project is now active");
      load();
    } catch (e) { toast.error("Could not activate draft"); } finally { setBusy(null); }
  };

  const handleDelete = async () => {
    setDeleteConfirm(false);
    setBusy("delete");
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Project deleted");
      nav("/app/projects");
    } catch (e) { toast.error("Delete failed"); } finally { setBusy(null); }
  };

  // ---- tracker actions ----
  const createStage = async () => {
    const name = newStageName.trim();
    if (!name) return;
    try {
      const created = await api.post(`/projects/${id}/tracker/stages`, {
        name,
        description: newStageDescription.trim() || null,
        status: "not_started",
        due_date: newStageDueDate || null,
      }).then(r => r.data);
      setNewStageName("");
      setNewStageDescription("");
      setNewStageDueDate("");
      setPendingAddStage(false);
      setTracker((prev) => ({
        ...prev,
        stages: [...(prev.stages || []), { ...created, milestones: [] }],
      }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add stage"); }
  };

  const deleteStage = async (stageId) => {
    try {
      await api.delete(`/projects/${id}/tracker/stages/${stageId}`);
      setTracker((prev) => ({
        ...prev,
        stages: (prev.stages || []).filter((s) => s.id !== stageId),
      }));
      if (selectedStageId === stageId) setSelectedStageId(null);
      toast.success("Stage deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete stage"); }
  };

  const updateStage = async (stageId, patch) => {
    setBusy(stageId);
    try {
      const updated = await api.patch(`/projects/${id}/tracker/stages/${stageId}`, patch).then(r => r.data);
      setTracker((prev) => ({
        ...prev,
        stages: (prev.stages || []).map((s) => s.id === stageId ? { ...s, ...updated } : s),
      }));
      if (patch.status === "in_progress") {
        setSelectedStageId(stageId);
      }
      toast.success("Stage updated");
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update stage");
      return false;
    } finally { setBusy(null); }
  };

  const selectStage = async (stage) => {
    setSelectedStageId(stage.id);
  };

  const startStage = async (stage) => {
    setSelectedStageId(stage.id);
    if (currentStage && currentStage.id !== stage.id) {
      setPendingStartStage(stage);
      return;
    }
    await updateStage(stage.id, { status: "in_progress" });
  };

  const confirmStartNextStage = async () => {
    if (!pendingStartStage || !currentStage) return;
    const nextStage = pendingStartStage;
    setPendingStartStage(null);
    const completed = await updateStage(currentStage.id, { status: "completed" });
    if (completed) await updateStage(nextStage.id, { status: "in_progress" });
  };

  const completeStage = async (stageId) => {
    setCompleteStageConfirm(null);
    await updateStage(stageId, { status: "completed" });
    const stageIndex = stages.findIndex((stage) => stage.id === stageId);
    const nextStage = stageIndex >= 0 ? stages[stageIndex + 1] : null;
    if (nextStage && nextStage.status === "not_started") {
      await updateStage(nextStage.id, { status: "in_progress" });
    }
    toast.success("Stage marked complete");
  };

  const createMilestone = async (stageId) => {
    const name = newMilestoneName.trim();
    if (!name) return;
    try {
      const created = await api.post(`/projects/${id}/tracker/stages/${stageId}/milestones`, {
        name,
        status: "not_started",
        due_date: newMilestoneDueDate || null,
      }).then(r => r.data);
      setNewMilestoneName("");
      setNewMilestoneDueDate("");
      setPendingAddMilestoneStage(null);
      setTracker((prev) => ({
        ...prev,
        stages: (prev.stages || []).map((s) =>
          s.id === stageId ? { ...s, milestones: [...(s.milestones || []), { ...created }] } : s
        ),
      }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add milestone"); }
  };

  const updateMilestone = async (stageId, milestoneId, patch) => {
    setBusy(milestoneId);
    try {
      const updated = await api.patch(`/projects/${id}/tracker/milestones/${milestoneId}`, patch).then(r => r.data);
      // If this was the last incomplete milestone, prompt to complete the stage.
      const stage = (tracker.stages || []).find((s) => s.id === stageId);
      const remaining = (stage.milestones || []).filter((m) => m.id !== milestoneId && m.status !== "completed");
      const isLast = remaining.length === 0 && (patch.status === "completed" || updated.status === "completed");
      setTracker((prev) => ({
        ...prev,
        stages: (prev.stages || []).map((s) =>
          s.id === stageId ? { ...s, milestones: (s.milestones || []).map((m) => m.id === milestoneId ? { ...m, ...updated } : m) } : s
        ),
      }));
      if (isLast && stage.status !== "completed") {
        setCompleteStageConfirm(stageId);
      }
      toast.success("Milestone updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update milestone"); } finally { setBusy(null); }
  };

  const deleteMilestone = async (stageId, milestoneId) => {
    try {
      await api.delete(`/projects/${id}/tracker/milestones/${milestoneId}`);
      setTracker((prev) => ({
        ...prev,
        stages: (prev.stages || []).map((s) =>
          s.id === stageId ? { ...s, milestones: (s.milestones || []).filter((m) => m.id !== milestoneId) } : s
        ),
      }));
      toast.success("Milestone deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete milestone"); }
  };

  // ---- notes ----
  const addNote = async () => {
    const content = newNote.trim();
    if (!content) return;
    setNoteBusy(true);
    try {
      const created = await api.post(`/projects/${id}/tracker/notes`, {
        content,
        tagged_user_ids: taggedMemberIds,
      }).then(r => r.data);
      setNewNote("");
      setTaggedMemberIds([]);
      setTracker((prev) => ({ ...prev, notes: [created, ...(prev.notes || [])] }));
      load(); // refresh recent updates
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add note"); } finally { setNoteBusy(false); }
  };

  const deleteNote = async (noteId) => {
    try {
      await api.delete(`/projects/${id}/tracker/notes/${noteId}`);
      setTracker((prev) => ({ ...prev, notes: (prev.notes || []).filter((n) => n.id !== noteId) }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete note"); }
  };

  const confirmDeleteNote = async () => {
    if (!pendingDeleteNote) return;
    const noteId = pendingDeleteNote.id;
    setPendingDeleteNote(null);
    await deleteNote(noteId);
  };

  const confirmDeleteMilestone = async () => {
    if (!pendingDeleteMilestone) return;
    const { stageId, milestoneId } = pendingDeleteMilestone;
    setPendingDeleteMilestone(null);
    await deleteMilestone(stageId, milestoneId);
  };

  const confirmDeleteStage = async () => {
    if (!pendingDeleteStage) return;
    const stageId = pendingDeleteStage.id;
    setPendingDeleteStage(null);
    await deleteStage(stageId);
  };

  const saveEditedStage = async () => {
    if (!pendingEditStage?.name.trim()) return;
    const stage = pendingEditStage;
    setPendingEditStage(null);
    await updateStage(stage.id, {
      name: stage.name.trim(),
      description: stage.description.trim(),
      due_date: stage.due_date || undefined,
    });
  };

  const openRecentResult = async (item) => {
    try {
      const result = await api.get(`/results/${item.id}`).then((response) => response.data);
      setActiveResult(result);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load result");
    }
  };

  // ---- derived ----
  const totalResults = newsTotal + tenderTotal;

  const stageMenuOpen = openStageMenu;

  const headerActions = (
    <div className="flex items-center gap-3">
      {!isOrgMember && (
        <button
          onClick={() => { setTab("details"); }}
          disabled={busy}
          data-testid="edit-project-button"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${busy
            ? "opacity-50 cursor-not-allowed"
            : "bg-[#0F1F3D] text-white hover:bg-[#172A4F]"
          }`}
        >
          <FileText className="h-4 w-4" /> Edit project
        </button>
      )}
      <div className="relative group">
        <button
          onClick={() => setOpenStageMenu((o) => !o)}
          disabled={busy}
          aria-label="Project actions"
          className={`inline-flex items-center justify-center h-9 w-9 rounded-md border transition-colors ${busy
            ? "opacity-50 cursor-not-allowed"
            : "border-[#E2E8F0] text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0F1F3D]"
          }`}
        >
          <MoreHorizontal className="h-5 w-5" />
        </button>
        {stageMenuOpen && (
          <div className="absolute right-0 mt-2 w-48 rounded-md border border-[#E2E8F0] bg-white shadow-lg py-1 z-50">
            {!isDraft && (
              <>
                <button onClick={() => { setOpenStageMenu(false); handleRun(); }} disabled={running || !project.is_active || isOrgMember}
                  className={`w-full inline-flex items-center gap-2 text-left px-4 py-2 text-sm transition-colors ${running || !project.is_active || isOrgMember
                    ? "text-[#94A3B8] cursor-not-allowed"
                    : "text-[#0F1F3D] hover:bg-[#F1F5F9]"
                  }`}>
                  {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}<span>Run now</span>
                </button>
                <button onClick={() => { setOpenStageMenu(false); handleToggleActive(); }}
                  className="w-full inline-flex items-center gap-2 text-left px-4 py-2 text-sm text-[#0F1F3D] hover:bg-[#F1F5F9]">
                  <Flag className="h-4 w-4" /> <span>{isPaused ? "Resume project" : "Pause project"}</span>
                </button>
              </>
            )}
            {isDraft && (
              <button onClick={() => { setOpenStageMenu(false); setActivateConfirm(true); }} disabled={isOrgMember}
                className={`w-full text-left px-4 py-2 text-sm transition-colors ${isOrgMember ? "text-[#94A3B8] cursor-not-allowed" : "text-[#0F1F3D] hover:bg-[#F1F5F9]"}`}>
                <Play className="h-4 w-4 mr-2" /> Activate draft
              </button>
            )}
            {!isOrgMember && (
              <button onClick={() => { setOpenStageMenu(false); setDeleteConfirm(true); }}
                className="w-full inline-flex items-center gap-2 text-left px-4 py-2 text-sm text-[#B91C1C] hover:bg-[#FEF2F2]">
                <Trash2 className="h-4 w-4" /> <span>Delete project</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const statusBadge = () => {
    if (isDraft) return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] text-[11px] font-semibold">Draft</span>;
    if (isPaused) return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#FECACA] text-[#B91C1C] text-[11px] font-semibold">Paused</span>;
    return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#ECFDF5] text-[#047857] text-[11px] font-semibold"><span className="h-1.5 w-1.5 rounded-full bg-[#047857]" /> Active</span>;
  };

  return (
    <div className="p-8 lg:p-12" data-testid="project-detail-page">
      {/* Back link */}
      <div className="flex items-center gap-2 mb-6">
        <Link to="/app/projects" className="inline-flex items-center gap-2 type-body-sm text-[#475569] hover:text-[#0F1F3D]">
          <ChevronRight className="h-4 w-4 rotate-180" /> Back to projects
        </Link>
      </div>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-8">
        <div className="flex items-center justify-between gap-4 w-full">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="type-h1 text-[#0F1F3D]">{project?.name || "Project"}</h1>
            {statusBadge()}
          </div>
          {headerActions}
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-8">
        <div className="flex pb-1">
          <button
            onClick={() => setTab("overview")}
            className={`mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${tab === "overview"
              ? "border-[#0F1F3D] text-[#0F1F3D]"
              : "border-transparent text-[#64748B] hover:text-[#0F1F3D] hover:bg-[#F8FAFC]"
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setTab("details")}
            className={`mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${tab === "details"
              ? "border-[#0F1F3D] text-[#0F1F3D]"
              : "border-transparent text-[#64748B] hover:text-[#0F1F3D] hover:bg-[#F8FAFC]"
            }`}
          >
            Details
          </button>
          <button
            onClick={() => setTab("tracker")}
            className={`mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${tab === "tracker"
              ? "border-[#0F1F3D] text-[#0F1F3D]"
              : "border-transparent text-[#64748B] hover:text-[#0F1F3D] hover:bg-[#F8FAFC]"
            }`}
          >
            Tracker
          </button>
        </div>
      </div>

      {/* ================= OVERVIEW ================= */}
      {tab === "overview" && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
            <button type="button" onClick={() => setTab("tracker")} className="text-left bg-white border border-[#E2E8F0] rounded-lg p-5 hover:border-[#94A3B8] transition-colors">
              <div className="flex items-start gap-3">
                <div className="self-center h-10 w-10 rounded-full bg-[#EFF6FF] text-[#1D4ED8] flex items-center justify-center"><FileText className="h-5 w-5" /></div>
                <div className="min-w-0">
                  <div className="type-caption text-[#64748B] min-h-[32px] flex items-center">Current stage</div>
                  <div className="type-body text-[#0F1F3D] font-semibold">{currentStage?.name || "Not started"}</div>
                </div>
              </div>
              <div className="type-caption text-[#64748B] mt-3">{stages.length} stage{stages.length !== 1 ? "s" : ""} · View tracker</div>
            </button>
            <Link
              to={`/app/feed?project_id=${id}&tab=actions&action_required=true&action_status=open`}
              className="bg-white border border-[#E2E8F0] rounded-lg p-5 hover:border-[#94A3B8] transition-colors"
            >
              <div className="flex items-start gap-3">
                <div className="self-center h-10 w-10 rounded-full bg-[#FEF3C7] text-[#B45309] flex items-center justify-center"><Sparkles className="h-5 w-5" /></div>
                <div className="min-w-0">
                  <div className="type-caption text-[#64748B] min-h-[32px] flex items-center">Strategic recommendations</div>
                  <div className="type-h3 text-[#0F1F3D] font-bold">{openRecommendationTotal}</div>
                </div>
              </div>
              <div className="type-caption text-[#64748B] mt-3">Open recommendations</div>
            </Link>
            <Link to={`/app/feed?project_id=${id}`} className="bg-white border border-[#E2E8F0] rounded-lg p-5 hover:border-[#94A3B8] transition-colors">
              <div className="flex items-start gap-3">
                <div className="self-center h-10 w-10 rounded-full bg-[#F1F5FF] text-[#4338CA] flex items-center justify-center"><Newspaper className="h-5 w-5" /></div>
                <div className="min-w-0">
                  <div className="type-caption text-[#64748B] min-h-[32px] flex items-center">News mentions</div>
                  <div className="type-h3 text-[#0F1F3D] font-bold">{newsTotal}</div>
                </div>
              </div>
              <div className="type-caption text-[#64748B] mt-3">Kept results · View intelligence feed</div>
            </Link>
            <Link to={`/app/tenders?project_id=${id}`} className="bg-white border border-[#E2E8F0] rounded-lg p-5 hover:border-[#94A3B8] transition-colors">
              <div className="flex items-start gap-3">
                <div className="self-center h-10 w-10 rounded-full bg-[#ECFDF5] text-[#047857] flex items-center justify-center"><Gavel className="h-5 w-5" /></div>
                <div className="min-w-0">
                  <div className="type-caption text-[#64748B] min-h-[32px] flex items-center">Relevant tenders</div>
                  <div className="type-h3 text-[#0F1F3D] font-bold">{tenderTotal}</div>
                </div>
              </div>
              <div className="type-caption text-[#64748B] mt-3">Open tenders · View tenders</div>
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-6">
            <div className="lg:col-span-3 bg-white border border-[#E2E8F0] rounded-lg p-6">
              <div className="type-body text-[#0F1F3D] font-semibold mb-3">Project summary</div>
              <p className="type-body text-[#475569] leading-relaxed">{project?.description || "No summary available."}</p>
            </div>
            <div className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-lg p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="type-body text-[#0F1F3D] font-semibold">Monitoring setup</div>
                <button type="button" onClick={() => setTab("details")} className="type-caption text-[#0F1F3D] hover:underline">Edit</button>
              </div>
              <div className="type-caption text-[#64748B] mb-2">Selected templates</div>
              <div className="flex flex-wrap gap-2 mb-4">
                {project?.template_ids?.length ? templatesMeta(project.template_ids).map((tm) => (
                  <span key={tm.label} className="px-3 py-1 rounded-full text-xs font-medium" style={{ background: tm.color + "15", color: tm.color }}>{tm.label}</span>
                )) : <span className="type-caption text-[#94A3B8]">No templates selected</span>}
              </div>
              <div className="border-t border-[#E2E8F0] pt-3">
                <div className="type-caption text-[#64748B]">Monitoring frequency</div>
                <div className="type-body text-[#0F1F3D] font-medium mt-1">{project?.schedule_hour != null ? `Daily at ${formatScheduleTime(project.schedule_hour, project.schedule_minute, project.schedule_timezone)}` : "Not scheduled"}</div>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="type-body text-[#0F1F3D] font-semibold">Recent activity</div>
              <Link to={`/app/feed?project_id=${id}`} className="type-caption text-[#0F1F3D] hover:underline">View all</Link>
            </div>
            {results.length ? (
              <div className="divide-y divide-[#E2E8F0]">
                {results.slice(0, 3).map((item) => {
                  const isTender = String(item.source || item.type || "").toLowerCase().includes("tender");
                  return (
                    <button key={item.id} type="button" onClick={() => isTender ? nav(`/app/tenders?project_id=${id}`) : nav(`/app/feed?project_id=${id}`)}
                      className="w-full grid grid-cols-1 md:grid-cols-[120px_100px_1fr_auto] gap-2 items-center py-3 text-left hover:bg-[#F8FAFC]">
                      <span className="type-caption text-[#64748B]">{fmtDateTime(item.published_at || item.run_date)}</span>
                      <span className={`w-fit px-2.5 py-1 rounded-full text-xs font-medium ${isTender ? "bg-[#FFF7ED] text-[#B45309]" : "bg-[#EFF6FF] text-[#1D4ED8]"}`}>{isTender ? "Tender" : "News"}</span>
                      <span className="type-body text-[#0F1F3D] line-clamp-1">{item.title || "Untitled result"}</span>
                      <span className="type-caption text-[#64748B]">{fmtRelative(item.published_at || item.run_date)}</span>
                    </button>
                  );
                })}
              </div>
            ) : <div className="text-center py-6 text-[#94A3B8] text-sm">No recent results.</div>}
          </div>
        </>
      )}

      {/* ================= DETAILS ================= */}
      {tab === "details" && (
        <div className="max-w-4xl mx-auto">
          <ProjectFormEditor projectId={id} onSave={load} />
        </div>
      )}

      {/* ================= TRACKER ================= */}
      {tab === "tracker" && (
        <>
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-lg font-semibold text-[#0F1F3D]">Project stage</div>
              {!isOrgMember && (
                <button onClick={() => setPendingAddStage(true)} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#E2E8F0] text-[#0F1F3D] text-sm hover:bg-[#F1F5F9]">
                  <CirclePlus className="h-3.5 w-3.5" /> Add stage
                </button>
              )}
            </div>
            <p className="text-sm font-normal leading-relaxed text-[#64748B] mb-4">Track the progress of this project across key stages. Update stage status, add notes and monitor key milestones.</p>

            {/* Stage progress bar */}
            <div className={`relative mx-4 mb-8 ${stages.length > 10 ? "overflow-x-auto pb-2" : ""}`}>
              <div
                className="flex w-full justify-between text-xs mt-2 px-4"
                style={stages.length > 10 ? { minWidth: `${stages.length * 96}px` } : undefined}
              >
                {stages.map((stage, idx) => {
                  const isCurrent = stage.id === currentStage?.id;
                  const completed = stage.status === "completed";
                  const cls = completed ? "text-[#047857]" : isCurrent ? "text-[#1D4ED8]" : "text-[#94A3B8]";
                  return (
                    <button
                      key={stage.id}
                      type="button"
                      onClick={() => selectStage(stage)}
                      className={`flex flex-col items-center gap-1 ${stages.length > 10 ? "shrink-0 w-24" : ""}`}
                    >
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 shrink-0 transition-all ${completed
                        ? "bg-[#047857] border-[#047857] text-white"
                        : isCurrent ? "bg-[#1D4ED8] border-[#1D4ED8] text-white"
                        : "bg-white border-[#CBD5E2] text-[#94A3B8]"
                      }`}>
                        {completed ? <Check className="h-3 w-3" /> : idx + 1}
                      </div>
                      <div className={`font-semibold ${cls} w-20 truncate text-center`}>{stage.name}</div>
                      <div className={cls}>{stageLabel(stage.status)}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Stage details */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-lg p-6">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <div className="flex items-center gap-2 min-w-0">
                  <Target className="h-5 w-5 text-[#0F1F3D]" />
                  <div className="type-body text-[#0F1F3D] font-semibold">
                    {stages.find((s) => s.id === (selectedStageId || currentStage?.id || firstStage?.id))?.name || "Stage"}
                  </div>
                  {(() => {
                    const headerStage = stages.find((s) => s.id === (selectedStageId || currentStage?.id || firstStage?.id));
                    return headerStage ? (
                      <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium ${stageStatusClass(headerStage.status)}`}>
                        {stageLabel(headerStage.status)}
                      </div>
                    ) : null;
                  })()}
                  </div>
                  {(() => {
                    const headerStage = stages.find((s) => s.id === (selectedStageId || currentStage?.id || firstStage?.id));
                    if (isOrgMember || !headerStage) return null;
                    return (
                      <div className="flex items-center gap-2">
                        {headerStage.status === "in_progress" && (
                          <button onClick={() => completeStage(headerStage.id)}
                            className="shrink-0 px-3 py-1.5 rounded-md border border-[#047857] text-[#047857] text-xs hover:bg-[#ECFDF5]">
                            Mark complete
                          </button>
                        )}
                        {headerStage.status === "not_started" && (
                          <button onClick={() => startStage(headerStage)}
                            className="shrink-0 px-3 py-1.5 rounded-md border border-[#1D4ED8] text-[#1D4ED8] text-xs hover:bg-[#EFF6FF]">
                            Start stage
                          </button>
                        )}
                        <div className="relative">
                          <button
                            onClick={(event) => {
                              event.stopPropagation();
                              setOpenStageActionMenu((current) => current === headerStage.id ? null : headerStage.id);
                            }}
                            className="px-2 py-1.5 text-[#64748B] hover:text-[#0F1F3D]"
                            aria-label="Stage actions"
                          >
                            ⋮
                          </button>
                          {openStageActionMenu === headerStage.id && (
                            <div className="absolute right-0 mt-1 w-40 rounded-md border border-[#E2E8F0] bg-white shadow-lg py-1 z-30">
                              <button
                                onClick={() => {
                                  setSelectedStageId(headerStage.id);
                                  setPendingEditStage({
                                    id: headerStage.id,
                                    name: headerStage.name || "",
                                    description: headerStage.description || "",
                                    due_date: headerStage.due_date || "",
                                  });
                                  setOpenStageActionMenu(null);
                                }}
                                className="w-full inline-flex items-center gap-2 px-3 py-2 text-left text-sm text-[#0F1F3D] hover:bg-[#F1F5F9]"
                              >
                                <FileText className="h-4 w-4" /> <span>Edit</span>
                              </button>
                              <button
                                onClick={() => { setOpenStageActionMenu(null); updateStage(headerStage.id, { status: "not_started" }); }}
                                className="w-full inline-flex items-center gap-2 px-3 py-2 text-left text-sm text-[#0F1F3D] hover:bg-[#F1F5F9]"
                              >
                                <Target className="h-4 w-4" /> <span>Reset status</span>
                              </button>
                              <button
                                onClick={() => { setOpenStageActionMenu(null); setPendingDeleteStage(headerStage); }}
                                className="w-full inline-flex items-center gap-2 px-3 py-2 text-left text-sm text-[#B91C1C] hover:bg-[#FEF2F2]"
                              >
                                <Trash2 className="h-4 w-4" /> <span>Delete</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
                {selectedStageId || currentStage || firstStage ? (
                  <div className="space-y-4">
                    {(() => {
                      const stage = stages.find((s) => s.id === (selectedStageId || currentStage?.id || firstStage?.id)) || null;
                      if (!stage) return null;
                      const stageMs = milestones[stage.id] || [];
                      const allDone = stageMs.length > 0 && stageMs.every((m) => m.status === "completed");
                      return (
                        <>
                          <div className="flex items-center justify-between gap-2">
                            <div>
                              <div className="type-body text-[#475569] max-w-xl">
                                {stage.description || ""}
                              </div>
                              {stage.due_date && isOverdue(stage.due_date) && stage.status !== "completed" && (
                                <div className="text-xs text-[#B91C1C]">Overdue</div>
                              )}
                              {stage.due_date && <div className="type-caption text-[#64748B] mt-1">Due {fmtDate(stage.due_date)}</div>}
                            </div>
                          </div>

                          {/* Stage rename / due date (inline edit) */}
                          {/* Key milestones */}
                          <div className="mt-6">
                            <div className="flex items-center justify-between mb-3">
                              <div className="type-caption text-[#64748B] font-semibold">Key milestones</div>
                              {!isOrgMember && (
                                <button onClick={(e) => { e.stopPropagation(); setPendingAddMilestoneStage(stage.id); }}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-[#E2E8F0] text-[#0F1F3D] text-xs hover:bg-[#F1F5F9]">
                                  <CirclePlus className="h-3 w-3" /> Add milestone
                                </button>
                              )}
                            </div>
                            {stageMs.length > 0 ? (
                              <ul className="space-y-2">
                                {stageMs.map((m) => (
                                  <li key={m.id} className="flex items-center gap-3 p-2 rounded-md border border-[#E2E8F0] bg-[#F8FAFC]">
                                    <div className="relative">
                                      <input type="checkbox"
                                        checked={m.status === "completed"}
                                        onChange={() => updateMilestone(stage.id, m.id, { status: m.status === "completed" ? "not_started" : "completed" })}
                                        disabled={isOrgMember}
                                        className="w-4 h-4 rounded border-[#CBD5E1] text-[#0F1F3D] focus:ring-0"
                                      />
                                      {m.status === "completed" && <div className="pointer-events-none absolute inset-0 flex items-center justify-center"><Check className="h-3 w-3 text-[#047857]" /></div>}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className={`text-sm truncate ${m.status === "completed" ? "line-through text-[#64748B]" : "text-[#0F1F3D]"}`}>{m.name}</div>
                                      {m.due_date && <div className={`text-xs ${isOverdue(m.due_date) && m.status !== "completed" ? "text-[#B91C1C] font-semibold" : "text-[#64748B]"}`}>Due {fmtDate(m.due_date)}</div>}
                                      <div className="type-caption text-[#64748B] mt-0.5">{stageLabel(m.status)}</div>
                                    </div>
                                    {!isOrgMember && (
                                      <button onClick={(e) => { e.stopPropagation(); setPendingDeleteMilestone({ stageId: stage.id, milestoneId: m.id }); }}
                                        className="p-1 text-[#94A3B8] hover:text-[#B91C1C]" aria-label="Delete milestone">
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </button>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <div className="text-center py-4 text-[#94A3B8] text-sm">No milestones yet.</div>
                            )}
                            {allDone && stage.status !== "completed" && !isOrgMember && (
                              <div className="mt-3 p-3 rounded-md bg-[#FEF3C7] border border-[#FDE68A]">
                                <div className="text-sm text-[#92400E]">All milestones for <b>{stage.name}</b> are complete.</div>
                                <button onClick={() => setCompleteStageConfirm(stage.id)}
                                  className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#92400E] underline underline-offset-4 hover:text-[#B45309]">
                                  Mark stage as complete
                                </button>
                              </div>
                            )}
                          </div>

                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="text-center py-12 text-[#94A3B8]">No stages yet. Add a stage to begin tracking.</div>
                )}
              </div>

              {/* Notes + recent updates sidebar */}
              <div className="lg:col-span-1">
                <div className="bg-white border border-[#E2E8F0] rounded-lg p-5 mb-6">
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-[#0F1F3D]" />
                      <div className="type-body text-[#0F1F3D] font-semibold">Notes</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setPendingAddNote(true)}
                      className="inline-flex items-center justify-center p-1 text-[#0F1F3D] hover:text-[#172A4F]"
                      aria-label="Add note"
                      title="Add note"
                    >
                      <CirclePlus className="h-5 w-5" />
                    </button>
                  </div>
                  {notes.length > 0 ? (
                    <ul className="max-h-64 overflow-y-auto space-y-3 pr-1">
                      {notes.map((n) => (
                        <li
                          key={n.id}
                          className={`p-3 rounded-md border ${
                            n.tagged_user_ids?.includes(user?.id)
                              ? "border-[#93C5FD] bg-[#EFF6FF] ring-1 ring-[#BFDBFE]"
                              : "border-[#E2E8F0] bg-[#F8FAFC]"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="type-caption font-semibold text-[#0F1F3D]">{n.user_name}</div>
                            <div className="flex items-center gap-2">
                              <div className="type-caption text-[#64748B]">{fmtRelative(n.created_at)}</div>
                              {!isOrgMember && (n.user_id === user?.id || isAdmin) && (
                                <button
                                  type="button"
                                  onClick={() => setPendingDeleteNote(n)}
                                  className="inline-flex items-center justify-center p-1 text-[#94A3B8] hover:text-[#B91C1C]"
                                  aria-label="Delete note"
                                  title="Delete note"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="type-body text-[#374151] text-sm leading-relaxed">{n.content}</div>
                          {n.tagged_user_names?.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {n.tagged_user_names.map((name) => (
                                <span key={name} className="inline-flex items-center rounded-full bg-[#EFF6FF] px-2 py-0.5 text-xs font-medium text-[#1D4ED8]">
                                  {name}
                                </span>
                              ))}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-center py-4 text-[#94A3B8] text-sm">No notes yet.</div>
                  )}
                </div>

                <div className="bg-white border border-[#E2E8F0] rounded-lg p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="type-body text-[#0F1F3D] font-semibold">Recent updates</div>
                  </div>
                  {recentUpdates.length > 0 ? (
                    <ul className="space-y-2">
                      {recentUpdates.slice(0, 5).map((item) => (
                        <li
                          key={item.id}
                          onClick={() => item.type !== "note" && openRecentResult(item)}
                          className={`p-2.5 rounded-md border border-[#E2E8F0] bg-[#F8FAFC] hover:bg-[#FFFFFF] transition-colors ${item.type !== "note" ? "cursor-pointer" : ""}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className={`h-2 w-2 rounded-full ${item.type === "news" ? "bg-[#0F1F3D]" : item.type === "tender" ? "bg-[#B45309]" : "bg-[#64748B]"}`} />
                              <div className="flex-1 min-w-0">
                                <div className="type-body text-sm text-[#0F1F3D] line-clamp-1">{item.title}</div>
                                <div className="flex items-center gap-1.5 text-xs text-[#64748B] mt-0.5">
                                  <span>{item.source}</span>
                                  <span>·</span>
                                  <span>{fmtRelative(item.date)}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          {/* {item.type === "note" && (
                            <div className="mt-1 type-body text-sm text-[#475569] line-clamp-1">{item.content}</div>
                          )} */}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-center py-6 text-[#94A3B8] text-sm">No recent updates.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Confirmation dialogs */}
      <AlertDialog open={deleteConfirm} onOpenChange={setDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete project?</AlertDialogTitle>
            <AlertDialogDescription>
              All results for this project will be permanently deleted. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={busy === "delete"} className="bg-[#EF4444] text-white hover:bg-[#DC2626]">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {runConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
          <div className="fixed inset-0 bg-[#0B1120]/60 backdrop-blur-sm" onClick={() => setRunConfirm(false)} />
          <div className="bg-white rounded-lg p-6 shadow-xl w-full max-w-md">
            <div className="type-body text-[#0F1F3D] font-semibold mb-2">Run project?</div>
            <div className="type-caption text-[#64748B] mb-4">This will run the entire project pipeline and may take several minutes.</div>
            <div className="flex gap-2">
              <button onClick={() => setRunConfirm(false)} className="flex-1 px-4 py-2 rounded-md border border-[#E2E8F0] text-[#475569] hover:bg-[#F8FAFC]">Cancel</button>
              <button onClick={handleRun} disabled={running} className="flex-1 px-4 py-2 rounded-md bg-[#0F1F3D] text-white hover:bg-[#172A4F] disabled:opacity-50">Run project</button>
            </div>
          </div>
        </div>
      )}
      {activateConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
          <div className="fixed inset-0 bg-[#0B1120]/60 backdrop-blur-sm" onClick={() => setActivateConfirm(false)} />
          <div className="bg-white rounded-lg p-6 shadow-xl w-full max-w-md">
            <div className="type-body text-[#0F1F3D] font-semibold mb-2">Activate draft?</div>
            <div className="type-caption text-[#64748B] mb-4">This will promote the draft to an active project and it will be included in scheduled runs.</div>
            <div className="flex gap-2">
              <button onClick={() => setActivateConfirm(false)} className="flex-1 px-4 py-2 rounded-md border border-[#E2E8F0] text-[#475569] hover:bg-[#F8FAFC]">Cancel</button>
              <button onClick={handleActivateDraft} disabled={busy === "pause"} className="flex-1 px-4 py-2 rounded-md bg-[#0F1F3D] text-white hover:bg-[#172A4F] disabled:opacity-50">Activate</button>
            </div>
          </div>
        </div>
      )}
      {completeStageConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
          <div className="fixed inset-0 bg-[#0B1120]/60 backdrop-blur-sm" onClick={() => setCompleteStageConfirm(null)} />
          <div className="relative z-10 bg-white rounded-lg p-6 shadow-xl w-full max-w-md">
            <div className="type-body text-[#0F1F3D] font-semibold mb-2">Complete stage?</div>
            <div className="type-caption text-[#64748B] mb-4">All milestones are done. Mark this stage as completed and move to the next stage?</div>
            <div className="flex gap-2">
              <button onClick={() => setCompleteStageConfirm(null)} className="flex-1 px-4 py-2 rounded-md border border-[#E2E8F0] text-[#475569] hover:bg-[#F8FAFC]">Not yet</button>
              <button onClick={() => completeStage(completeStageConfirm)} className="flex-1 px-4 py-2 rounded-md bg-[#0F1F3D] text-white hover:bg-[#172A4F]">Mark complete</button>
            </div>
          </div>
        </div>
      )}
      {pendingStartStage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
          <div className="fixed inset-0 bg-[#0B1120]/60" onClick={() => setPendingStartStage(null)} />
          <div className="bg-white rounded-lg p-6 shadow-xl w-full max-w-md relative z-10">
            <div className="type-body text-[#0F1F3D] font-semibold mb-2">
              Start {pendingStartStage.name}?
            </div>
            <div className="type-caption text-[#64748B] mb-4">
              {currentStage?.name} is currently in progress. Mark it as complete before starting {pendingStartStage.name}?
            </div>
            <div className="flex gap-2">
              <button onClick={() => setPendingStartStage(null)} className="flex-1 px-4 py-2 rounded-md border border-[#E2E8F0] text-[#475569] hover:bg-[#F8FAFC]">
                Not yet
              </button>
              <button onClick={confirmStartNextStage} className="flex-1 px-4 py-2 rounded-md bg-[#0F1F3D] text-white hover:bg-[#172A4F]">
                Complete and start
              </button>
            </div>
          </div>
        </div>
      )}
      <AlertDialog open={!!pendingDeleteStage} onOpenChange={(open) => { if (!open) setPendingDeleteStage(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete stage?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDeleteStage && <>Delete {pendingDeleteStage.name} and all of its milestones? This cannot be undone.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteStage} className="bg-[#EF4444] text-white hover:bg-[#DC2626]">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={!!pendingEditStage}
        onOpenChange={(open) => { if (!open) setPendingEditStage(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Edit stage</AlertDialogTitle>
            <AlertDialogDescription>
              Update the stage name, description, or due date.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {pendingEditStage && (
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-[#475569]">Stage name</label>
                <input
                  autoFocus
                  value={pendingEditStage.name}
                  onChange={(event) => setPendingEditStage((stage) => ({ ...stage, name: event.target.value }))}
                  className="mt-1 w-full px-3 py-2 rounded-md border border-[#E2E8F0] text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#475569]">Description (optional)</label>
                <textarea
                  value={pendingEditStage.description}
                  onChange={(event) => setPendingEditStage((stage) => ({ ...stage, description: event.target.value }))}
                  rows={3}
                  className="mt-1 w-full px-3 py-2 rounded-md border border-[#E2E8F0] text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#475569]">Due date</label>
                <input
                  type="date"
                  value={pendingEditStage.due_date}
                  onChange={(event) => setPendingEditStage((stage) => ({ ...stage, due_date: event.target.value }))}
                  className="mt-1 w-full px-3 py-2 rounded-md border border-[#E2E8F0] text-sm"
                />
              </div>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={!pendingEditStage?.name.trim()} onClick={saveEditedStage}>
              Save changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!pendingDeleteMilestone} onOpenChange={(open) => { if (!open) setPendingDeleteMilestone(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this milestone?</AlertDialogTitle>
            <AlertDialogDescription>
              This milestone and its progress will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteMilestone} className="bg-[#EF4444] text-white hover:bg-[#DC2626]">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ResultModal
        item={activeResult}
        project={project}
        open={!!activeResult}
        onClose={() => setActiveResult(null)}
        isBookmarked={false}
        onToggleBookmark={() => {}}
        onFeedback={() => {}}
      />
      <AlertDialog
        open={pendingAddNote}
        onOpenChange={(open) => {
          if (!open) {
            setPendingAddNote(false);
            setNewNote("");
            setTaggedMemberIds([]);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Add note</AlertDialogTitle>
            <AlertDialogDescription>
              Add a note to this project.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div>
            <label className="text-sm font-medium text-[#475569]">Note</label>
            <textarea
              autoFocus
              value={newNote}
              onChange={(event) => setNewNote(event.target.value)}
              rows={4}
              placeholder="Write a note"
              className="mt-1 w-full px-3 py-2 rounded-md border border-[#E2E8F0] text-sm focus:border-[#0F1F3D]"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-[#475569]">Tag organization member (optional)</label>
            <div className="relative mt-1" ref={memberDropdownRef}>
              <button
                type="button"
                onClick={() => setMemberDropdownOpen((open) => !open)}
                className="flex min-h-[44px] w-full items-center justify-between rounded-md border border-[#E2E8F0] bg-white px-3 py-2.5 text-left text-sm focus:outline-none focus:ring-2 focus:ring-[#0F1F3D]"
              >
                <span className={taggedMemberIds.length ? "text-[#0F1F3D]" : "text-[#64748B]"}>
                  {taggedMemberIds.length
                    ? `${taggedMemberIds.length} member${taggedMemberIds.length === 1 ? "" : "s"} selected`
                    : "Select organization members"}
                </span>
                <ChevronDown className={`h-4 w-4 text-[#64748B] transition-transform ${memberDropdownOpen ? "rotate-180" : ""}`} />
              </button>
              {memberDropdownOpen && (
                <div className="absolute z-20 mt-1 max-h-52 w-full overflow-y-auto rounded-md border border-[#E2E8F0] bg-white p-1 shadow-lg">
                  {orgMembers.map((member) => {
                    const selected = taggedMemberIds.includes(member.id);
                    return (
                      <button
                        key={member.id}
                        type="button"
                        onClick={() => setTaggedMemberIds((ids) => selected
                          ? ids.filter((id) => id !== member.id)
                          : [...ids, member.id]
                        )}
                        className="flex w-full items-start gap-2 rounded-md px-3 py-2 text-left hover:bg-[#F1F5F9]"
                      >
                        <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border text-[11px] ${selected ? "border-[#0F1F3D] bg-[#0F1F3D] text-white" : "border-[#CBD5E1]"}`}>
                          {selected ? "✓" : ""}
                        </span>
                        <span className="min-w-0 truncate text-sm text-[#0F1F3D]">{member.full_name || member.email}</span>
                      </button>
                    );
                  })}
                  {!orgMembers.length && <div className="px-3 py-2 text-sm text-[#94A3B8]">No organization members found.</div>}
                </div>
              )}
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={noteBusy || !newNote.trim()}
              onClick={async (event) => {
                event.preventDefault();
                await addNote();
                setPendingAddNote(false);
              }}
            >
              {noteBusy ? "Adding…" : "Add note"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!pendingDeleteNote} onOpenChange={(open) => { if (!open) setPendingDeleteNote(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this note?</AlertDialogTitle>
            <AlertDialogDescription>
              This note will be permanently deleted. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteNote} className="bg-[#EF4444] text-white hover:bg-[#DC2626]">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={!!pendingAddMilestoneStage}
        onOpenChange={(open) => {
          if (!open) {
            setPendingAddMilestoneStage(null);
            setNewMilestoneName("");
            setNewMilestoneDueDate("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Add milestone</AlertDialogTitle>
            <AlertDialogDescription>
              Enter the milestone name and optional due date.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-[#475569]">Milestone name</label>
              <input
                autoFocus
                value={newMilestoneName}
                onChange={(event) => setNewMilestoneName(event.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-md border border-[#E2E8F0] text-sm"
                placeholder="Milestone name"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#475569]">Due date</label>
              <input
                type="date"
                value={newMilestoneDueDate}
                onChange={(event) => setNewMilestoneDueDate(event.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-md border border-[#E2E8F0] text-sm"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!newMilestoneName.trim()}
              onClick={() => createMilestone(pendingAddMilestoneStage)}
            >
              Add milestone
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={pendingAddStage}
        onOpenChange={(open) => {
          if (!open) {
            setPendingAddStage(false);
            setNewStageName("");
            setNewStageDescription("");
            setNewStageDueDate("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Add stage</AlertDialogTitle>
            <AlertDialogDescription>
              Enter the stage name and optional due date.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-[#475569]">Stage name</label>
              <input
                autoFocus
                value={newStageName}
                onChange={(event) => setNewStageName(event.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-md border border-[#E2E8F0] text-sm"
                placeholder="Stage name"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#475569]">Description (optional)</label>
              <textarea
                value={newStageDescription}
                onChange={(event) => setNewStageDescription(event.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-md border border-[#E2E8F0] text-sm"
                placeholder="Describe this stage"
                rows={3}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#475569]">Due date</label>
              <input
                type="date"
                value={newStageDueDate}
                onChange={(event) => setNewStageDueDate(event.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-md border border-[#E2E8F0] text-sm"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={!newStageName.trim()} onClick={createStage}>
              Add stage
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function scoreClass(score) {
  if (score >= 80) return "bg-[#FEF2F2] text-[#B91C1C]";
  if (score >= 60) return "bg-[#FFFBEB] text-[#B45309]";
  if (score >= 45) return "bg-[#F1F5F9] text-[#475569]";
  return "bg-[#F1F5F9] text-[#475569]";
}

function fmtDate(iso) {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); } catch { return iso; }
}
